import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError , errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useActiveFlat } from "../lib/active-flat";
import type { Flat } from "../lib/types";
import { Button, ErrorBanner, Spinner } from "../components/ui";

type InvitePreview = { id: string; name: string; memberCount: number };

export function JoinFlatPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const { setFlat, reload } = useActiveFlat();

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!code) {
      setLoading(false);
      setLoadError("Missing invite code.");
      return;
    }
    (async () => {
      try {
        const data = await api.get<InvitePreview>(`/flat/invite/${code}`, { auth: false });
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err instanceof ApiError && err.status === 404
              ? "This invite link is invalid or has expired."
              : err instanceof ApiError
                ? err.message
                : "Couldn't load the invite. Check your connection.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const handleJoin = async () => {
    if (!code) return;
    setJoinError(null);
    setJoining(true);
    try {
      const flat = await api.post<Flat>("/flat/join", { inviteCode: code });
      setFlat(flat);
      await reload();
      navigate("/", { replace: true });
    } catch (err) {
      setJoinError(
        errMsg(err, "Couldn't join the flat. Please try again."));
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-brand-50 via-white to-slate-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          {loading ? (
            <div className="py-6">
              <Spinner />
              <p className="mt-3 text-sm text-slate-500">Loading invite…</p>
            </div>
          ) : loadError ? (
            <>
              <h1 className="text-lg font-semibold text-slate-900">Invite unavailable</h1>
              <p className="mt-2 text-sm text-slate-600">{loadError}</p>
              <Button
                variant="secondary"
                className="mt-5"
                onClick={() => navigate("/", { replace: true })}
              >
                Go home
              </Button>
            </>
          ) : preview ? (
            <>
              <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-brand-600 text-2xl font-bold text-white shadow-lg shadow-brand-600/30">
                {preview.name[0]?.toUpperCase() ?? "F"}
              </div>
              <p className="text-xs uppercase tracking-wider text-slate-500">You're invited to join</p>
              <h1 className="mt-1 text-2xl font-semibold text-slate-900">{preview.name}</h1>
              <p className="mt-1 text-sm text-slate-500">
                {preview.memberCount} member{preview.memberCount === 1 ? "" : "s"} so far
              </p>

              <div className="mt-6">
                {auth.status === "loading" ? (
                  <Spinner />
                ) : auth.status === "anonymous" ? (
                  <Button
                    className="w-full"
                    onClick={() => navigate(`/login?invite=${code}`)}
                  >
                    Sign in to join
                  </Button>
                ) : !auth.user.name ? (
                  <Button className="w-full" onClick={() => navigate("/onboarding")}>
                    Set up your profile to continue
                  </Button>
                ) : (
                  <>
                    <Button className="w-full" loading={joining} onClick={handleJoin}>
                      Join this flat
                    </Button>
                    {joinError && (
                      <div className="mt-3 text-left">
                        <ErrorBanner message={joinError} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
